import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd';
import { ArrowLeftOutlined, EyeOutlined, SaveOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import api from '@/services/api.service';
import recognitionService, {
  DAY_COUNT_METHODS,
  INITIAL_POSTING_MODES,
  PARTIAL_MONTH_POLICIES,
  POSTING_GRANULARITIES,
  POSTING_MODES,
  RECOGNITION_METHODS,
  type ContractInput,
  type SchedulePreview,
} from '@/services/financeRecognition.service';
import { useChartAccountsByType } from '@/hooks/useSalesInvoice';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';
import { useProjectsForSelection } from '@/hooks/useProject';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { useCurrency } from '@/contexts/CurrencyContext';
import { DAY_COUNT_LABEL, METHOD_LABEL, POSTING_MODE_LABEL, accountLabel, amount, apiErrorMessage, contractPath, options, useRecognitionAccess } from './prepaidDisplay';
import {
  AllocationEditor,
  CustomScheduleEditor,
  SchedulePreviewTable,
  allocationProblem,
  type AllocationRow,
  type CustomLineRow,
} from './PrepaidFormParts';

const { Title, Text } = Typography;

function unwrap<T>(res: any): T {
  const data = res?.data ?? res;
  return (data?.data ?? data) as T;
}

type Mode = 'MANUAL' | 'OPENING_BALANCE';

const d = (v: dayjs.Dayjs | null | undefined) => (v ? dayjs(v).format('YYYY-MM-DD') : null);
const strip = <T extends { key: string }>(rows: T[]) => rows.map(({ key: _k, ...rest }) => rest);

const PrepaidContractFormPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const access = useRecognitionAccess();
  const { baseCurrency, allCurrencies } = useCurrency();
  const [form] = Form.useForm();
  const [mode, setMode] = useState<Mode>(params.get('mode') === 'opening' ? 'OPENING_BALANCE' : 'MANUAL');
  const [allocations, setAllocations] = useState<AllocationRow[]>([]);
  const [customLines, setCustomLines] = useState<CustomLineRow[]>([]);
  const [preview, setPreview] = useState<SchedulePreview | null>(null);

  const method = Form.useWatch('recognition_method', form);
  const currency = Form.useWatch('transaction_currency', form);
  const initialMode = Form.useWatch('initial_posting_mode', form);
  const transactionAmount = Form.useWatch('transaction_amount', form);
  const recognizableAmount = Form.useWatch('recognizable_amount', form);
  const openingOriginal = Form.useWatch(['opening', 'original_amount'], form);
  const openingRecognized = Form.useWatch(['opening', 'historically_recognized_amount'], form);

  const { data: existing, isLoading } = useQuery({
    queryKey: ['recognition', 'contract', id],
    queryFn: () => recognitionService.getContract(id!),
    enabled: isEdit,
  });
  const { data: existingLines } = useQuery({
    queryKey: ['recognition', 'contract', id, 'schedule', 'all'],
    queryFn: () => recognitionService.contractSchedule(id!, { limit: 500 }),
    enabled: isEdit && existing?.recognition_method === 'CUSTOM_SCHEDULE',
  });
  const { data: categories = [] } = useQuery({ queryKey: ['recognition', 'categories'], queryFn: () => recognitionService.listCategories() });
  const { data: policy } = useQuery({ queryKey: ['recognition', 'policy'], queryFn: () => recognitionService.getPolicy() });
  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers-list-prepaid'],
    queryFn: async () => unwrap<any[]>(await api.get('/procurement/suppliers')) ?? [],
  });
  const { data: expenseAccounts = [] } = useChartAccountsByType('Expense', false);
  const { data: assetAccounts = [] } = useChartAccountsByType('Asset', false);
  const { data: liabilityAccounts = [] } = useChartAccountsByType('Liability', false);
  const { data: departments = [] } = useDepartmentsForSelection();
  const { data: projects = [] } = useProjectsForSelection();

  const expenseOptions = useMemo(() => expenseAccounts.map((a) => ({ value: a.id, label: accountLabel(a.account_code, a.account_name) })), [expenseAccounts]);
  const assetOptions = useMemo(() => assetAccounts.map((a) => ({ value: a.id, label: accountLabel(a.account_code, a.account_name) })), [assetAccounts]);
  const clearingOptions = useMemo(
    () => [...liabilityAccounts, ...assetAccounts].map((a) => ({ value: a.id, label: accountLabel(a.account_code, a.account_name) })),
    [liabilityAccounts, assetAccounts]
  );
  const projectOptions = useMemo(
    () =>
      (projects as any[]).map((p) => ({ value: p.id, label: p.project_number ? `${p.project_number} - ${p.project_name}` : p.project_name ?? p.id })),
    [projects]
  );
  const departmentOptions = useMemo(
    () => departments.map((x) => ({ value: x.id, label: `${x.department_code} - ${x.department_name}` })),
    [departments]
  );
  const currencyOptions = useMemo(
    () => allCurrencies.map((c) => ({ value: c.currency_code, label: `${c.currency_code}${c.currency_name ? ` - ${c.currency_name}` : ''}` })),
    [allCurrencies]
  );

  useEffect(() => {
    if (isEdit || !policy) return;
    form.setFieldsValue({ posting_mode: form.getFieldValue('posting_mode') ?? policy.default_posting_mode });
  }, [policy, isEdit, form]);

  useEffect(() => {
    if (isEdit || !baseCurrency) return;
    if (!form.getFieldValue('transaction_currency')) form.setFieldsValue({ transaction_currency: baseCurrency.currency_code, exchange_rate: 1 });
  }, [baseCurrency, isEdit, form]);

  useEffect(() => {
    if (!existing) return;
    const opening = existing.source_type === 'OPENING_BALANCE';
    setMode(opening ? 'OPENING_BALANCE' : 'MANUAL');
    form.setFieldsValue({
      category_id: existing.category_id ?? undefined,
      description: existing.description,
      notes: existing.notes,
      supplier_id: existing.supplier_id ?? undefined,
      supplier_invoice_number: existing.supplier_invoice_number,
      invoice_date: existing.invoice_date ? dayjs(existing.invoice_date) : null,
      purchase_order_reference: existing.purchase_order_reference,
      contract_reference: existing.contract_reference,
      department_id: existing.department_id ?? undefined,
      cost_center: existing.cost_center,
      project_id: existing.project_id ?? undefined,
      transaction_currency: existing.transaction_currency,
      exchange_rate: Number(existing.exchange_rate),
      transaction_amount: existing.transaction_amount,
      recognizable_amount: existing.recognizable_amount !== existing.transaction_amount ? existing.recognizable_amount : null,
      service_start_date: dayjs(existing.service_start_date),
      service_end_date: dayjs(existing.service_end_date),
      recognition_start_date: existing.recognition_start_date !== existing.service_start_date ? dayjs(existing.recognition_start_date) : null,
      recognition_end_date: existing.recognition_end_date !== existing.service_end_date ? dayjs(existing.recognition_end_date) : null,
      recognition_method: existing.recognition_method,
      day_count_method: existing.day_count_method,
      partial_month_policy: existing.partial_month_policy,
      posting_mode: existing.posting_mode,
      posting_granularity: existing.posting_granularity ?? undefined,
      prepaid_asset_account_id: existing.prepaid_asset_account_id,
      expense_account_id: existing.expense_account_id,
      initial_posting_mode: existing.initial_posting_mode,
      source_clearing_account_id: existing.source_clearing_account_id ?? undefined,
      opening: opening
        ? {
            original_amount: existing.original_amount,
            historically_recognized_amount: existing.historically_recognized_amount,
            migration_date: existing.migration_date ? dayjs(existing.migration_date) : null,
            original_service_start: existing.original_service_start ? dayjs(existing.original_service_start) : null,
            original_service_end: existing.original_service_end ? dayjs(existing.original_service_end) : null,
          }
        : undefined,
    });
    setAllocations(
      existing.allocations.map((a) => ({
        key: a.id,
        allocation_basis: a.allocation_basis,
        percentage: a.percentage,
        fixed_amount: a.fixed_amount,
        expense_account_id: a.expense_account_id,
        cost_center: a.cost_center,
        project_id: a.project_id,
        department_id: a.department_id,
        branch_id: a.branch_id,
        effective_from: a.effective_from,
        effective_to: a.effective_to,
      }))
    );
  }, [existing, form]);

  useEffect(() => {
    if (!existingLines) return;
    setCustomLines(
      existingLines.data
        .filter((l) => l.status !== 'CANCELLED')
        .map((l) => ({ key: l.id, from: l.recognition_from_date, to: l.recognition_to_date, amount: l.planned_amount }))
    );
  }, [existingLines]);

  const recognizableTarget = mode === 'OPENING_BALANCE'
    ? openingOriginal != null && openingRecognized != null
      ? (Math.round(Number(openingOriginal) * 100) - Math.round(Number(openingRecognized) * 100)) / 100
      : null
    : recognizableAmount || transactionAmount || null;

  const buildInput = (values: any): ContractInput => {
    const input: ContractInput = {
      category_id: values.category_id ?? null,
      description: values.description,
      notes: values.notes ?? null,
      supplier_id: values.supplier_id ?? null,
      supplier_invoice_number: values.supplier_invoice_number ?? null,
      invoice_date: d(values.invoice_date),
      purchase_order_reference: values.purchase_order_reference ?? null,
      contract_reference: values.contract_reference ?? null,
      department_id: values.department_id ?? null,
      cost_center: values.cost_center ?? null,
      project_id: values.project_id ?? null,
      transaction_currency: values.transaction_currency,
      exchange_rate: values.exchange_rate,
      recognition_method: values.recognition_method,
      day_count_method: values.day_count_method,
      partial_month_policy: values.partial_month_policy,
      posting_mode: values.posting_mode,
      posting_granularity: values.posting_granularity ?? null,
      prepaid_asset_account_id: values.prepaid_asset_account_id ?? null,
      expense_account_id: values.expense_account_id ?? null,
      allocations: strip(allocations),
      custom_lines: values.recognition_method === 'CUSTOM_SCHEDULE' ? strip(customLines) : undefined,
      version: existing?.version,
    };
    if (mode === 'OPENING_BALANCE') {
      input.opening = {
        original_amount: values.opening.original_amount,
        historically_recognized_amount: values.opening.historically_recognized_amount,
        migration_date: d(values.opening.migration_date)!,
        original_service_start: d(values.opening.original_service_start)!,
        original_service_end: d(values.opening.original_service_end)!,
      };
    } else {
      input.transaction_amount = values.transaction_amount;
      input.recognizable_amount = values.recognizable_amount || null;
      input.service_start_date = d(values.service_start_date)!;
      input.service_end_date = d(values.service_end_date)!;
      input.recognition_start_date = d(values.recognition_start_date);
      input.recognition_end_date = d(values.recognition_end_date);
      input.initial_posting_mode = values.initial_posting_mode ?? 'NONE';
      input.source_clearing_account_id = values.initial_posting_mode === 'POST_ON_ACTIVATION' ? values.source_clearing_account_id ?? null : null;
    }
    return input;
  };

  const previewMutation = useMutation({
    mutationFn: (input: ContractInput) => recognitionService.previewSchedule(input),
    onSuccess: (p) => setPreview(p),
    onError: (e) => {
      setPreview(null);
      message.error(apiErrorMessage(e, 'Preview failed'));
    },
  });

  const saveMutation = useMutation({
    mutationFn: (input: ContractInput) => (isEdit ? recognitionService.updateContract(id!, input) : recognitionService.createContract(input)),
    onSuccess: (c) => {
      message.success(isEdit ? 'Prepaid updated' : `Prepaid ${c.recognition_number} created as draft`);
      queryClient.invalidateQueries({ queryKey: ['recognition'] });
      navigate(contractPath(c.id));
    },
    onError: (e) => message.error(apiErrorMessage(e, 'Save failed')),
  });

  const validated = async () => {
    const values = await form.validateFields();
    const problem = allocationProblem(allocations, recognizableTarget, t);
    if (problem) {
      message.error(problem);
      return null;
    }
    if (values.recognition_method === 'CUSTOM_SCHEDULE' && customLines.length === 0) {
      message.error(t('addCustomScheduleLines'));
      return null;
    }
    return buildInput(values);
  };

  const onPreview = async () => {
    try {
      const input = await validated();
      if (input) previewMutation.mutate(input);
    } catch {
      /* field validation shown inline */
    }
  };

  const onSave = async () => {
    try {
      const input = await validated();
      if (input) saveMutation.mutate(input);
    } catch {
      /* field validation shown inline */
    }
  };

  const applyCategory = (categoryId?: string) => {
    const cat = categories.find((c) => c.id === categoryId);
    if (!cat) return;
    const patch: Record<string, unknown> = {};
    if (cat.default_prepaid_asset_account_id && !form.getFieldValue('prepaid_asset_account_id')) patch.prepaid_asset_account_id = cat.default_prepaid_asset_account_id;
    if (cat.default_expense_account_id && !form.getFieldValue('expense_account_id')) patch.expense_account_id = cat.default_expense_account_id;
    if (cat.default_recognition_method) patch.recognition_method = cat.default_recognition_method;
    if (cat.default_posting_mode) patch.posting_mode = cat.default_posting_mode;
    form.setFieldsValue(patch);
    if (!form.getFieldValue('description')) form.setFieldsValue({ description: cat.name });
  };

  if (!(isEdit ? access.canEdit : access.canCreate)) {
    return <Alert type="warning" showIcon style={{ margin: 24 }} message={`You need ${isEdit ? 'finance.recognition.edit' : 'finance.recognition.create'} to ${isEdit ? 'edit' : 'create'} prepaid contracts.`} />;
  }
  if (isEdit && isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }
  if (isEdit && existing && existing.status !== 'DRAFT') {
    return (
      <Alert
        style={{ margin: 24 }}
        type="info"
        showIcon
        message={`${existing.recognition_number} is ${existing.status.toLowerCase().replace(/_/g, ' ')} and can no longer be edited.`}
        description="Use an adjustment (revision, extension, change of GL or dimensions…) from the contract page."
        action={<Button onClick={() => navigate(contractPath(existing.id))}>{t('openContract')}</Button>}
      />
    );
  }

  const isForeign = currency && baseCurrency && currency !== baseCurrency.currency_code;
  const fromInvoice = existing?.source_type === 'PURCHASE_INVOICE';

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }} wrap>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(isEdit ? contractPath(id!) : '/fin/prepaid/contracts')}>
          {tc('back')}
        </Button>
        <Title level={4} style={{ margin: 0 }}>
          {isEdit ? `Edit ${existing?.recognition_number ?? 'prepaid'}` : 'New prepaid expense'}
        </Title>
        {!isEdit && (
          <Segmented<Mode>
            value={mode}
            onChange={(v) => {
              setMode(v);
              setPreview(null);
            }}
            options={[
              { value: 'MANUAL', label: t('manualPrepayment') },
              { value: 'OPENING_BALANCE', label: t('openingBalanceMigration') },
            ]}
          />
        )}
      </Space>

      {mode === 'OPENING_BALANCE' && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('openingBalanceMigration')}
          description="Use for prepayments already in the General Ledger before go-live. Only the remaining balance (original − already recognised) is scheduled from the migration date; no capitalisation journal is posted."
        />
      )}
      {fromInvoice && (
        <Alert type="info" showIcon style={{ marginBottom: 16 }} message={`Created from ${existing?.source_document_number ?? 'a purchase invoice'}. Amounts and currency follow the invoice line.`} />
      )}

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          recognition_method: 'DAILY_PRO_RATA',
          day_count_method: 'ACTUAL_CALENDAR_DAYS',
          partial_month_policy: 'PRO_RATA',
          initial_posting_mode: 'NONE',
          exchange_rate: 1,
        }}
        onValuesChange={() => preview && setPreview(null)}
      >
        <Card title={t('prepayment')} style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="category_id" label={t('category')}>
                <Select allowClear showSearch optionFilterProp="label" options={categories.map((c) => ({ value: c.id, label: c.name }))} onChange={applyCategory} />
              </Form.Item>
            </Col>
            <Col xs={24} md={16}>
              <Form.Item name="description" label={t('description')} rules={[{ required: true, whitespace: true, max: 500 }]}>
                <Input placeholder={t('exampleInsurancePolicy')} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="supplier_id" label={t('supplier')}>
                <Select allowClear showSearch optionFilterProp="label" options={suppliers.map((s: any) => ({ value: s.id, label: s.supplier_name }))} />
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item name="supplier_invoice_number" label={t('supplierInvoiceNo')}>
                <Input disabled={fromInvoice} />
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item name="invoice_date" label={t('invoiceDate')}>
                <DatePicker style={{ width: '100%' }} disabled={fromInvoice} />
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item name="purchase_order_reference" label={t('poReference')}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item name="contract_reference" label={t('contractPolicyRef')}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="notes" label={t('notes')}>
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title={t('amountAndServicePeriod')} style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={12} md={4}>
              <Form.Item name="transaction_currency" label={t('currency')} rules={[{ required: true }]}>
                <Select
                  showSearch
                  disabled={fromInvoice}
                  options={currencyOptions}
                  onChange={(c) => {
                    if (c === baseCurrency?.currency_code) form.setFieldsValue({ exchange_rate: 1 });
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item
                name="exchange_rate"
                label={`Rate to ${baseCurrency?.currency_code ?? 'base'}`}
                rules={[{ required: true }]}
                extra={isForeign ? 'Historical rate; recognition never revalues the prepaid asset.' : undefined}
              >
                <InputNumber min={0.000001} step={0.0001} precision={6} style={{ width: '100%' }} disabled={!isForeign || fromInvoice} />
              </Form.Item>
            </Col>
            {mode === 'MANUAL' ? (
              <>
                <Col xs={12} md={4}>
                  <Form.Item name="transaction_amount" label={t('contractAmount')} rules={[{ required: true }]}>
                    <InputNumber min={0.01} precision={2} stringMode style={{ width: '100%' }} disabled={fromInvoice} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Item name="recognizable_amount" label={t('recognisableAmount')} extra="Blank = full amount">
                    <InputNumber min={0.01} precision={2} stringMode style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Item name="service_start_date" label={t('serviceStart')} rules={[{ required: true }]}>
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Item
                    name="service_end_date"
                    label={t('serviceEnd')}
                    dependencies={['service_start_date']}
                    rules={[
                      { required: true },
                      ({ getFieldValue }) => ({
                        validator: (_, v) =>
                          !v || !getFieldValue('service_start_date') || !dayjs(v).isBefore(getFieldValue('service_start_date'), 'day')
                            ? Promise.resolve()
                            : Promise.reject(new Error('Must be on or after the service start')),
                      }),
                    ]}
                  >
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Item name="recognition_start_date" label={t('recognitionStart')} extra="Blank = service start">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Item name="recognition_end_date" label={t('recognitionEnd')} extra="Blank = service end">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </>
            ) : (
              <>
                <Col xs={12} md={4}>
                  <Form.Item name={['opening', 'original_amount']} label={t('originalAmount')} rules={[{ required: true }]}>
                    <InputNumber min={0.01} precision={2} stringMode style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Item name={['opening', 'historically_recognized_amount']} label={t('alreadyRecognised')} rules={[{ required: true }]}>
                    <InputNumber min={0} precision={2} stringMode style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Item label={t('openingRemaining')}>
                    <Input readOnly value={recognizableTarget != null ? amount(recognizableTarget) : ''} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Item name={['opening', 'original_service_start']} label={t('originalServiceStart')} rules={[{ required: true }]}>
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Item name={['opening', 'original_service_end']} label={t('originalServiceEnd')} rules={[{ required: true }]}>
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Item name={['opening', 'migration_date']} label={t('migrationDate')} rules={[{ required: true }]} extra="Recognition starts here">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>
        </Card>

        <Card title={t('recognition')} style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={6}>
              <Form.Item name="recognition_method" label={t('recognitionMethod')} rules={[{ required: true }]}>
                <Select options={options(RECOGNITION_METHODS, METHOD_LABEL)} />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="day_count_method" label={t('dayCount')} rules={[{ required: true }]}>
                <Select options={options(DAY_COUNT_METHODS, DAY_COUNT_LABEL)} disabled={method === 'CUSTOM_SCHEDULE'} />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="partial_month_policy" label={t('partialMonth')} rules={[{ required: true }]}>
                <Select options={options(PARTIAL_MONTH_POLICIES)} disabled={method === 'CUSTOM_SCHEDULE' || method === 'DAILY_PRO_RATA'} />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item
                name="posting_mode"
                label={t('postingMode')}
                rules={[{ required: true }]}
                extra={policy && !policy.allow_auto_post ? 'Auto-post is disabled by the company policy' : undefined}
              >
                <Select
                  options={options(POSTING_MODES, POSTING_MODE_LABEL).map((o) => ({ ...o, disabled: o.value === 'AUTO_POST' && !!policy && !policy.allow_auto_post }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="posting_granularity" label={t('postingGranularity')} extra="Blank = company policy">
                <Select allowClear options={options(POSTING_GRANULARITIES)} />
              </Form.Item>
            </Col>
          </Row>
          {method === 'CUSTOM_SCHEDULE' && (
            <Card size="small" type="inner" title={t('customSchedule')} style={{ marginTop: 8 }}>
              <CustomScheduleEditor rows={customLines} onChange={setCustomLines} target={recognizableTarget} />
            </Card>
          )}
        </Card>

        <Card title={t('accounting')} style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="prepaid_asset_account_id" label={t('prepaidAssetAccount')} rules={[{ required: true, message: t('selectPrepaidAssetOrCategory') }]}>
                <Select showSearch optionFilterProp="label" options={assetOptions} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="expense_account_id" label={t('expenseAccount')} rules={[{ required: true, message: t('selectExpenseOrCategory') }]}>
                <Select showSearch optionFilterProp="label" options={expenseOptions} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="department_id" label={t('department')}>
                <DepartmentSelect allowClear />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="project_id" label={t('project')}>
                <Select allowClear showSearch optionFilterProp="label" options={projectOptions} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="cost_center" label={t('costCentre')}>
                <Input />
              </Form.Item>
            </Col>
            {mode === 'MANUAL' && !fromInvoice && (
              <>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="initial_posting_mode"
                    label={t('prepaidAssetInGl')}
                    extra={initialMode === 'POST_ON_ACTIVATION' ? 'Activation posts Dr Prepaid asset / Cr clearing account' : 'The prepayment is already booked in the GL'}
                  >
                    <Select
                      options={INITIAL_POSTING_MODES.map((v) => ({
                        value: v,
                        label: v === 'NONE' ? 'Already in GL (no capitalisation JV)' : 'Post capitalisation JV on activation',
                      }))}
                    />
                  </Form.Item>
                </Col>
                {initialMode === 'POST_ON_ACTIVATION' && (
                  <Col xs={24} md={8}>
                    <Form.Item name="source_clearing_account_id" label={t('clearingAccountCredit')} rules={[{ required: true }]}>
                      <Select showSearch optionFilterProp="label" options={clearingOptions} />
                    </Form.Item>
                  </Col>
                )}
              </>
            )}
          </Row>
        </Card>

        <Card title={t('allocation')} style={{ marginBottom: 16 }}>
          <AllocationEditor
            rows={allocations}
            onChange={(rows) => {
              setAllocations(rows);
              setPreview(null);
            }}
            expenseOptions={expenseOptions}
            projectOptions={projectOptions}
            departmentOptions={departmentOptions}
            recognizable={recognizableTarget}
          />
        </Card>

        <Card
          title={t('schedulePreview')}
          style={{ marginBottom: 16 }}
          extra={
            <Button icon={<EyeOutlined />} loading={previewMutation.isPending} onClick={onPreview}>
              {t('previewSchedule')}
            </Button>
          }
        >
          <SchedulePreviewTable preview={preview} currency={currency} loading={previewMutation.isPending} />
        </Card>

        <Space>
          <Button type="primary" icon={<SaveOutlined />} loading={saveMutation.isPending} onClick={onSave}>
            {isEdit ? 'Save changes' : 'Save draft'}
          </Button>
          <Text type="secondary">{t('savedContractsStayDraft')}</Text>
        </Space>
      </Form>
    </div>
  );
};

export default PrepaidContractFormPage;
